import {
  Pagination,
  Switch,
  Table,
  Tag,
  DatePicker,
  Dropdown,
  Select,
  Tabs,
} from 'antd';
import {Button} from '@dabafinance/react-components';
import styles from './brokerBackOffice.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {
  GET_ALL_BROKER_BACK_OFFICE,
  GET_ALL_BROKERS,
} from 'graphql/queries/stocks';
import {brokerOptionLabel} from '../brokerVenues';
import {useMemo, useState} from 'react';
import moment from 'moment';
import dotsIcon from 'icons/dots.svg';
import DBModal from 'components/modal/modal';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {toast} from 'react-toastify';
import {fullyDecodeURL} from 'utils/helper';
import {UPDATE_BROKER_BACK_OFFICE} from 'graphql/mutations/stocks';
import {ConfirmModal} from '../ViewOrders/HandleOrders';
import {BrokersTab} from './BrokersTab';

export const BrokerBackOffice = () => {
  const [activeTab, setActiveTab] = useState('BACK_OFFICE');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [used, setUsed] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [brokerId, setBrokerId] = useState(null);
  const [updateBrokerModal, setUpdateBrokerModal] = useState({
    open: false,
    content: null,
  });
  const [downloadModal, setDownloadModal] = useState({
    open: false,
    type: null,
    url: null,
  });

  const [presignMediaURL, {loading: presignMediaURLLoading}] = useMutation(
    PRE_SIGN_MEDIA_URL,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: getAllBrokers, loading: loadingAllBrokers} = useQuery(
    GET_ALL_BROKERS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [updateBrokerBackoffice, {loading: updateBrokerBackofficeLoading}] =
    useMutation(UPDATE_BROKER_BACK_OFFICE, {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    });

  const {data, loading, refetch} = useQuery(GET_ALL_BROKER_BACK_OFFICE, {
    variables: {
      input: {
        page: 1,
        limit: 10,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const handleDownload = () => {
    let decodedFilename = null;
    let bucket = null;
    if (downloadModal.url) {
      if (downloadModal.url.includes('?AWS')) {
        bucket = downloadModal.url.split('.s3')[0].replace('https://', '');
        const filename = downloadModal.url.split('amazonaws.com/')[1];
        decodedFilename = fullyDecodeURL(filename.split('?AWS')[0]);

        presignMediaURL({
          variables: {
            input: {
              filename: decodedFilename,
              bucket,
            },
          },
        })
          .then(({data: {presignMediaURL}}) => {
            if (presignMediaURL.__typename === 'Error') {
              toast.error(presignMediaURL.message);
            } else {
              toast.success('Presigned URL Generated Successfully');
              window.open(presignMediaURL?.data?.url, '_blank');
            }
          })
          .catch(error =>
            // The second argument to `toast.error` is options, not a message —
            // the reason was being dropped on the floor.
            toast.error(error?.message || 'Something went wrong'),
          );
      } else {
        return window.open(downloadModal.url, '_blank');
      }
    }
  };

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      input: {
        page,
        limit: pageSize,
        used,
        ...(brokerId && {
          id: brokerId,
        }),
        ...(startDate && {
          startDate: moment(startDate).startOf('day').toISOString(),
        }),
        ...(endDate && {
          endDate: moment(endDate).endOf('day').toISOString(),
        }),
      },
    });
  };

  const onToggleUsed = checked => {
    setUsed(checked);
  };

  const onChangeStartDate = value => {
    setStartDate(value || null);
  };

  const onChangeEndDate = value => {
    setEndDate(value || null);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setLimit(10);
    refetch({
      input: {
        page: 1,
        limit: 10,
        ...(brokerId && {
          id: brokerId,
        }),
        used,
        ...(startDate && {
          startDate: moment(startDate).startOf('day').toISOString(),
        }),
        ...(endDate && {
          endDate: moment(endDate).endOf('day').toISOString(),
        }),
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Broker',
        key: 'broker',
        fixed: 'left',
        render: (_, record) => record?.broker?.name || '-̶',
      },
      {
        title: 'Used',
        key: 'used',
        dataIndex: 'used',
        render: used => (
          <Tag color={used ? 'green' : 'red'}>{used ? 'Yes' : 'No'}</Tag>
        ),
      },
      {
        title: 'Instrument',
        key: 'assetClass',
        dataIndex: 'assetClass',
        render: (_, record) =>
          record?.broker?.assetClass?.replace(/_/g, ' ') || '-',
      },
      {
        title: 'Period',
        key: 'period',
        render: (_, record) => {
          const start = record?.startDate
            ? moment(record.startDate).format('Do MMM, YYYY')
            : '-';
          const end = record?.endDate
            ? moment(record.endDate).format('Do MMM, YYYY')
            : '-';
          return `${start} → ${end}`;
        },
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: value =>
          value ? moment(value).format('Do MMM, YYYY HH:mm') : '-',
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: value =>
          value ? moment(value).format('Do MMM, YYYY HH:mm') : '-',
      },
      {
        title: 'Action',
        key: 'action',
        render: (text, record) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: 0,
                  label: record?.used ? 'Mark as Unused' : 'Mark as Used',
                  onClick: () => {
                    setUpdateBrokerModal({
                      open: true,
                      content: record,
                    });
                  },
                },
                {
                  key: 1,
                  label: 'Orders in Filter Group',
                  disabled: !record?.orders,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'ORDERS',
                      url: record?.orders,
                    });
                  },
                },
                {
                  key: 2,
                  label: 'Orders to be Replaced',
                  disabled: !record?.replace,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'REPLACEMENTS',
                      url: record?.replace,
                    });
                  },
                },
                {
                  key: 3,
                  label: 'Orders to be Zero Filled',
                  disabled: !record?.zerofilled,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'ZERO_FILLED',
                      url: record?.zerofilled,
                    });
                  },
                },
                {
                  key: 9,
                  label: 'Orders to be Ignored',
                  disabled: !record?.pending,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'PENDING',
                      url: record?.pending,
                    });
                  },
                },
                {
                  key: 10,
                  label: 'Orders to be Marked as Filled',
                  disabled: !record?.filled,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'FILLED',
                      url: record?.filled,
                    });
                  },
                },
                {
                  key: 4,
                  label: 'History used',
                  disabled: !record?.history,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'HISTORY',
                      url: record?.history,
                    });
                  },
                },
                {
                  key: 5,
                  label: 'Statement used',
                  disabled: !record?.statement,
                  onClick: () => {
                    setDownloadModal({
                      open: true,
                      type: 'STATEMENT',
                      url: record?.statement,
                    });
                  },
                },
              ],
            }}>
            <div>
              <img src={dotsIcon} alt="dots" />
            </div>
          </Dropdown>
        ),
      },
    ],
    [used, currentPage, limit],
  );

  const total = data?.getBrokerBackoffice?.pagination?.total;
  const {TabPane} = Tabs;

  return (
    <div className={styles.brokerBackOffice}>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Back Office" key="BACK_OFFICE">
          <div className={styles.header}>
            <div className={styles.filters}>
              <span className={styles.filterLabel}>Broker</span>
              <Select
                allowClear
                // Wide enough for "name — venue · asset class" without an
                // ellipsis, since the venue is what separates two same-name
                // brokers and is the part that would get cut.
                style={{width: 280}}
                placeholder="Select Broker"
                loading={loadingAllBrokers}
                options={getAllBrokers?.getBrokers?.data.map(item => ({
                  label: brokerOptionLabel(item),
                  value: item.id,
                }))}
                value={brokerId}
                onChange={setBrokerId}
              />
              <span className={styles.filterLabel}>Start date</span>
              <DatePicker
                allowClear
                size="small"
                value={startDate}
                onChange={onChangeStartDate}
              />
              <span className={styles.filterLabel}>End date</span>
              <DatePicker
                allowClear
                size="small"
                value={endDate}
                onChange={onChangeEndDate}
              />
              <span className={styles.filterLabel}>
                Filter by <strong>USED</strong>
              </span>
              <Switch size="small" checked={used} onChange={onToggleUsed} />
              <Button label="Apply Filters" onClick={applyFilters} />
            </div>
          </div>
          <div className={styles.tableContainer}>
            <Table
              rowKey="id"
              columns={columns}
              loading={loading}
              className="antd-table"
              dataSource={data?.getBrokerBackoffice?.data}
              pagination={false}
            />
            <Pagination
              current={currentPage}
              pageSize={limit}
              showSizeChanger
              showQuickJumper
              total={total}
              onChange={handlePageChange}
              className="ant-table-pagination"
              style={{marginTop: 16, textAlign: 'right'}}
            />
          </div>
        </TabPane>
        <TabPane tab="Brokers" key="BROKERS">
          <BrokersTab />
        </TabPane>
      </Tabs>
      <DBModal
        isOpen={downloadModal.open}
        handleClose={() => setDownloadModal({open: false, type: null})}
        width={600}
        content={
          <div className="confirm-modal">
            <h1 className={styles.modalHeader}>
              Download {downloadModal.type?.replace(/_/g, ' ')}
            </h1>
            <p>
              Click the button below to download the{' '}
              {downloadModal.type?.replace(/_/g, ' ')} CSV file
            </p>
            <div className="buttons">
              <Button
                label="Cancel"
                type="secondary"
                onClick={() =>
                  setDownloadModal({open: false, type: null, url: null})
                }
              />
              <Button
                label="Download"
                onClick={handleDownload}
                disabled={presignMediaURLLoading}
              />
            </div>
          </div>
        }
      />
      <ConfirmModal
        openModal={updateBrokerModal.open}
        setOpenModal={() => setUpdateBrokerModal({open: false, content: null})}
        loading={updateBrokerBackofficeLoading}
        title="Update Used Status"
        confirmText="Are you sure you want to update this Back Office used status?"
        handleOperation={() => {
          updateBrokerBackoffice({
            variables: {
              input: {
                used: !updateBrokerModal.content.used,
                id: updateBrokerModal.content.id,
              },
            },
          })
            .then(({data: {updateBrokerBackoffice}}) => {
              if (updateBrokerBackoffice.__typename === 'Error') {
                toast.error(updateBrokerBackoffice.message);
              } else {
                toast.success(updateBrokerBackoffice.message);
                setUpdateBrokerModal({open: false, content: null});
                refetch();
              }
            })
            .catch(error => toast.error(error.message));
        }}
      />
    </div>
  );
};
